import React, { useContext, useEffect, useState } from 'react'
import { Document, Page, Text, View, Image, StyleSheet, pdf } from '@react-pdf/renderer'
import api from '../../../services/api'
import { MainContext } from '../../../context/MainContext'

const B = 0.5
const BC = '#000'

const styles = StyleSheet.create({
    page: {
        paddingBottom: 40,
        paddingTop: 19,
        paddingLeft: 20,
        paddingRight: 20,
        fontSize: 8,
        flexDirection: 'column',
    },
    title: {
        textAlign: 'center',
        fontWeight: 'bold',
        marginBottom: 10,
        fontSize: 14,
    },
    subtitle: {
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 10,
    },
    header: {
        alignItems: 'center',
        marginBottom: 10,
    },
    headerLogo: {
        width: 50,
        height: 50,
        marginBottom: 4,
    },
    headerText: {
        textAlign: 'center',
        width: '100%',
    },
    clubName: {
        fontWeight: 'bold',
        fontSize: 12,
        marginBottom: 2,
        textAlign: 'center',
    },
    clubInfo: {
        fontSize: 8,
        textAlign: 'center',
    },
    tableRow: {
        flexDirection: 'row',
    },
    tableHeader: {
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold',
    },
    tableCell: {
        padding: 5,
        borderRightWidth: B,
        borderRightColor: BC,
        borderBottomWidth: B,
        borderBottomColor: BC,
        borderTopWidth: B,
        borderTopColor: BC,
        borderLeftWidth: B,
        borderLeftColor: BC,
    },
})

const ResultsPrinting: React.FC<any> = ({ eventEntryId, entryIds, filterMonth, preloadedPages, childToParent }: any) => {
    const { club, setLoading } = useContext(MainContext)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [pages, setPages] = useState<any[]>([])

    const formatScoreRange = (min: number, max: number): string => {
        if (min && max) return ` (${min}-${max})`
        if (max) return ` (>= ${max})`
        if (min) return ` (<= ${min})`
        return ''
    }

    const groupResults = (results: any[]) => {
        const grouped = results.reduce((acc: any, result: any) => {
            const cat = result.eventCategory?.category_name || 'Sem Categoria'
            if (!acc[cat]) acc[cat] = { eventCategory: result.eventCategory, results: [] }
            acc[cat].results.push(result)
            return acc
        }, {})
        return Object.keys(grouped).sort().map((categoryName) => ({
            categoryName,
            eventCategory: grouped[categoryName].eventCategory,
            results: grouped[categoryName].results,
        }))
    }

    async function loadData() {
        if (preloadedPages) {
            setPages(preloadedPages)
            return
        }
        const ids: any[] = entryIds?.length > 0 ? entryIds : [eventEntryId]
        const loaded: any[] = []

        for (const id of ids) {
            const [entryRes, resultsRes] = await Promise.all([
                api.get(`/api/event_entries/${id}`),
                api.get(`/api/event_results/report/${id}`),
            ])
            let results = resultsRes.data
            if (filterMonth) {
                results = results.filter((r: any) => {
                    const date = r.created_at || r.updated_at
                    return date && new Date(date).getMonth() + 1 === filterMonth
                })
            }
            loaded.push({ eventEntry: entryRes.data, resultsByCategory: groupResults(results) })
        }
        setPages(loaded)
    }

    const handleOpenPDF = async () => {
        if (dataLoaded) {
            const blob = await pdf(<ResultsPDF />).toBlob()
            const url = URL.createObjectURL(blob)
            window.open(url)
        }
    }

    useEffect(() => {
        setLoading(true)
        loadData().then(() => setDataLoaded(true))
    }, [])

    useEffect(() => {
        if (dataLoaded) {
            handleOpenPDF().then(() => {
                setLoading(false)
                if (childToParent) {
                    childToParent('imprimiu')
                }
            })
        }
    }, [dataLoaded])

    const ResultsPDF = () => (
        <Document>
            {pages.map(({ eventEntry, resultsByCategory }, pageIdx) => (
                <Page key={pageIdx} style={styles.page} size="A4">
                    <View style={styles.header}>
                        <Image style={styles.headerLogo} src="/logo192.png" />
                        <View style={styles.headerText}>
                            <Text style={styles.clubName}>{club?.fantasia}</Text>
                            <Text style={styles.clubInfo}>CNPJ: {club?.cnpj}</Text>
                            <Text style={styles.clubInfo}>
                                {club?.address?.street}, {club?.address?.number}
                                {club?.address?.complement ? `, ${club?.address?.complement}` : ''} - {club?.address?.city?.name} / {club?.address?.city?.state?.abbreviation} | Tel: {club?.phone}
                            </Text>
                        </View>
                    </View>

                    <Text style={styles.subtitle}>
                        {eventEntry?.matchDate?.eventStage?.event?.title} - {eventEntry?.matchDate?.eventStage?.title}
                    </Text>

                    {resultsByCategory.map((category: any, idx: number) => (
                        <View key={idx}>
                            <View style={[styles.tableRow, { marginTop: 10 }]} wrap={false}>
                                <Text style={[styles.tableCell, { width: '100%' }]}>
                                    Ranking {eventEntry?.matchDate?.eventStage?.event?.title} - Cat {category.categoryName}
                                    {formatScoreRange(category.eventCategory?.min_score, category.eventCategory?.max_score)}
                                </Text>
                            </View>
                            <View style={[styles.tableRow, styles.tableHeader]} wrap={false}>
                                <Text style={[styles.tableCell, { width: '10%', textAlign: 'center' }]}>Pos.</Text>
                                <Text style={[styles.tableCell, { width: '50%' }]}>Atleta</Text>
                                <Text style={[styles.tableCell, { width: '20%', textAlign: 'center' }]}>Calibre</Text>
                                <Text style={[styles.tableCell, { width: '20%', textAlign: 'center' }]}>Total</Text>
                            </View>
                            {category.results.map((result: any, index: number) => (
                                <View key={result.id} style={styles.tableRow} wrap={false}>
                                    <Text style={[styles.tableCell, { width: '10%', textAlign: 'center' }]}>{index + 1}º</Text>
                                    <Text style={[styles.tableCell, { width: '50%' }]}>{result.athlete?.full_name}</Text>
                                    <Text style={[styles.tableCell, { width: '20%', textAlign: 'center' }]}>
                                        {(() => {
                                            if (!result.caliber) return '-'
                                            const isSteelGauge = eventEntry?.matchDate?.eventStage?.event?.eventType?.modality === 'steel_gauge'
                                            const formatted = result.caliber.replace(/, /g, ' / ')
                                            const isMulti = result.caliber.includes(',')
                                            return isSteelGauge && !isMulti ? `${formatted} / 12` : formatted
                                        })()}
                                    </Text>
                                    <Text style={[styles.tableCell, { width: '20%', textAlign: 'center' }]}>{result.shot_totals}</Text>
                                </View>
                            ))}
                        </View>
                    ))}
                </Page>
            ))}
        </Document>
    )

    return null
}

export default ResultsPrinting
